import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePaywallPreview } from "@/hooks/usePaywallPreview";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { trackConversion } from "@/lib/conversionTracking";

const VISIT_KEY_PREFIX = "reviewboost_visit_count_";

export function ReturningUserBanner() {
  const { user, isFounder, profile } = useAuth();
  const { data } = usePaywallPreview();
  const { openPaywall } = usePaywallModal();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!user?.id || isFounder || profile?.subscription_status?.toLowerCase() === "active" || profile?.subscription_status?.toLowerCase() === "pro") {
      return;
    }
    const key = `${VISIT_KEY_PREFIX}${user.id}`;
    let count = 0;
    try {
      const raw = localStorage.getItem(key);
      if (raw) count = parseInt(raw, 10) || 0;
    } catch {
      /* ignore */
    }
    count += 1;
    try {
      localStorage.setItem(key, String(count));
    } catch {
      /* ignore */
    }
    setShowBanner(count >= 2);
  }, [user?.id, isFounder, profile?.subscription_status]);

  const businessName = data?.businessName ?? "your shop";
  const insightCount = data?.biRecommendationsCount ?? 0;
  const survivalDays = data?.survivalDays ?? 0;
  const deadStockValue = data?.deadStockValue ?? 0;
  const hasShopData = survivalDays > 0 || deadStockValue > 0;
  // Do not show when there is no stock/activity — avoids misleading "X insights" from stale cache or bad data
  if (!showBanner || !user || isFounder || insightCount <= 0 || !hasShopData) return null;

  const handleClick = () => {
    trackConversion("returning_banner_clicked", { businessName });
    openPaywall("returning_banner");
  };

  return (
    <button
      type="button"
      data-testid="returning-user-banner"
      onClick={handleClick}
      className="w-full text-left rounded-lg border border-primary/40 bg-primary/10 hover:bg-primary/15 px-4 py-3 transition-colors"
    >
      <p className="text-sm font-medium text-foreground">
        Since yesterday, your BI Analyst found{" "}
        <span data-testid="returning-banner-insight-count" className="font-bold">{insightCount} new insights</span> about{" "}
        <span className="font-bold">{businessName}</span>. See them →
        <span data-testid="returning-banner-cta" className="sr-only">See insights</span>
      </p>
    </button>
  );
}
